import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Ttm} from '../../classes/ttm';
import {TtmService} from '../../services/ttm.service';
import {BehaviorSubject, map} from 'rxjs';
import {CodaltComponent} from '../codalt/codalt.component';
import {TtmStatus} from '../../classes/ttmstatus';
import {TtmType} from '../../classes/ttmtype';
import {User} from '../../classes/user';
import {distance, point} from '@turf/turf';
import {AuthorisationService} from '../../services/auth/authorisation.service';
import {Features} from '../../services/auth/features';

@Component({
    selector: 'app-ttm-history-dialog',
    templateUrl: './ttm-history-dialog.component.html',
    styleUrls: ['./ttm-history-dialog.component.scss'],
})
export class TtmHistoryDialogComponent extends CodaltComponent implements OnInit {
    private ttmHistorySubject: BehaviorSubject<TtmHistory[]> = new BehaviorSubject<TtmHistory[]>([]);
    ttmHistory$ = this.ttmHistorySubject.asObservable();

    displayedColumns: string[] = ['created_at', 'field', 'old_value', 'new_value', 'changed_by_id', 'actions'];

    translate = new Map<string, string>([
        ['amount', 'Aantal'],
        ['amount_changed', 'Aantal aangepast'],
        ['comment', 'Opmerking'],
        ['comment_show_on_status_change', 'Opmerking tonen bij statuswijziging'],
        ['status_key', 'Status'],
        ['type_key', 'Soort'],
    ]);

    constructor(
        private ttmSrvc: TtmService,
        public dialogRef: MatDialogRef<TtmHistoryDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            ttm: Ttm;
            ttmStatusses: TtmStatus[];
            ttmTypes: TtmType[];
        },
    ) {
        super();
    }

    ngOnInit() {
        if (!AuthorisationService.hasFeature(Features.ShowSensetiveInformation)) {
            this.displayedColumns = [...this.displayedColumns.filter(column => column !== 'changed_by_id')];
        }
        ['weighting_one', 'weighting_two', 'weighting_three'].forEach((weightningName) => {
            this.translate.set(weightningName, this.data.ttm.type[`${weightningName}_name`]);
        });
        this.subscriptions.add(
            this.ttmSrvc
                .getTtmHistory(this.data.ttm.id)
                .pipe(
                    map((response) => {
                        response.data = response.data.map((history) => {
                            if (history.field === 'status_key') {
                                if (history.new_value) {
                                    const newStatus = this.data.ttmStatusses.find((status) => status.key === history.new_value);
                                    if (newStatus) {
                                        history.new_value = newStatus.name;
                                    }
                                }

                                if (history.old_value) {
                                    const oldStatus = this.data.ttmStatusses.find((status) => status.key === history.old_value);
                                    if (oldStatus) {
                                        history.old_value = oldStatus.name;
                                    }
                                }
                            }

                            if (history.field === 'type_key') {
                                if (history.new_value) {
                                    const newType = this.data.ttmTypes.find((type) => type.key === history.new_value);
                                    if (newType) {
                                        history.new_value = newType.name;
                                    }
                                }

                                if (history.old_value) {
                                    const oldType = this.data.ttmTypes.find((type) => type.key === history.old_value);
                                    if (oldType) {
                                        history.old_value = oldType.name;
                                    }
                                }
                            }

                            if (history.field.startsWith('weighting_') || history.field === 'comment_show_on_status_change') {
                                const customFieldName = this.data.ttmTypes.find((field) => field.key === this.data.ttm.type_key)?.[history.field + '_name'];
                                if (customFieldName) {
                                    history.field = customFieldName;
                                }
                                const translateValue = (value) => (value === '1' || value === 'true' || value === true ? 'Ja' : 'Nee');

                                history.new_value = translateValue(history.new_value);
                                history.old_value = translateValue(history.old_value);
                            }

                            // on history is a position object available when status was changed with location
                            // calculate distance from ttm to position and show it in a new field
                            if (history.field === 'status_key' && history['position']) {
                                const ttmEntity = this.data.ttm.entities[0];

                                // GeoJSON is [lon, lat]
                                const ttmCoordinates = ttmEntity.gnd.coordinates[0]; // [lon, lat]

                                const rawPosition = history['position'].coordinates; // currently [lat, lon] based on your log
                                const position = [rawPosition[1], rawPosition[0]]; // convert to [lon, lat]

                                const km = distance(point(ttmCoordinates as any), point(position), {units: 'kilometers'});
                                history['distance'] = km;

                                if (!this.displayedColumns.includes('distance')) {
                                    this.displayedColumns.splice(this.displayedColumns.indexOf('actions'), 0, 'distance');
                                }
                            }

                            return history;
                        });
                        return response;
                    }),
                )
                .subscribe((response) => {
                    this.ttmHistorySubject.next(response.data);
                }),
        );
    }

    onClose(): void {
        this.dialogRef.close();
    }

    openGoogleMaps(history: TtmHistory): void {
        if (history['position'] && history['position'].coordinates) {
            const [lat, lon] = history['position'].coordinates;
            window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lon}`, '_blank');
        }
    }

    showOnInternalMap(): void {
        this.dialogRef.close(this.ttmHistorySubject.getValue());
    }

    hasLocation(history: TtmHistory): boolean {
        return !!history['position'];
    }
}

export interface TtmHistory {
    id: number;
    ttm_id: number;
    field: string;
    old_value: string | null;
    new_value: string;
    changed_by_id: number;
    changed_by: User;
    created_at: Date;
    updated_at: Date;
}
